import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useDemoResetFeed } from '../../hooks/useDemoResetFeed';
import ScopeSync from './ScopeSync';
import NodeOfflineNotice from './NodeOfflineNotice';
import ConnectingOverlay from '../ConnectingOverlay';
import { useNode } from '../../contexts/NodeContext';
import { NodeStreamProvider } from '../../contexts/NodeStreamContext';
import { isNodeScoped } from '../../constants/nodeScopedPaths';
import { TitleBarProvider } from '@trinetra/shared';

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { nodeConnecting, dismissConnecting, selectedNode, selectedNodeId } = useNode();
  const { pathname } = useLocation();
  /**
   * The search page wants the width: a filter column, the answer and a tracking dock side by
   * side. So the sidebar folds on the way IN and goes back to however it was on the way OUT —
   * the operator's own choice everywhere else is not overwritten, and the toggle still works on
   * the search page itself.
   */
  const onSearchPage = pathname === '/search' || pathname.startsWith('/search/');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(onSearchPage);
  const collapsedBeforeSearch = useRef<boolean | null>(onSearchPage ? false : null);
  useEffect(() => {
    if (onSearchPage) {
      setSidebarCollapsed((prev) => {
        if (collapsedBeforeSearch.current === null) collapsedBeforeSearch.current = prev;
        return true;
      });
    } else if (collapsedBeforeSearch.current !== null) {
      setSidebarCollapsed(collapsedBeforeSearch.current);
      collapsedBeforeSearch.current = null;
    }
  }, [onSearchPage]);

  // Guard, not just decoration: the sidebar greys these entries out, but a bookmark, the back
  // button, or a node that drops while the page is open all get here without touching the nav.
  // Rendered in place rather than redirected, so the URL survives and the page comes back on
  // its own when the node reconnects.
  // Feeds the title bar's demo banner. Here rather than in the header because this is the
  // component mounted on every authenticated page, and the banner must be right on all of
  // them — the wipe it warns about takes attendance and reports, not just alerts.
  useDemoResetFeed(selectedNodeId ?? null);

  const blockedByOfflineNode = !!selectedNode && selectedNode.status !== 'online' && isNodeScoped(pathname);

  return (
    <NodeStreamProvider>
    <TitleBarProvider>
    <ScopeSync />
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <Sidebar
        collapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed((prev) => !prev)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <Header onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
        {/* No per-page scope banner any more: the title bar names the site permanently, so a
            strip repeating it on every scoped page was duplication — and every page gets that
            vertical space back. */}
        {/* Tight on purpose: the page title is in the title bar now (PageTitle), so the
            padding is only a gutter. PAGE_FILL_H assumes exactly this — change both together. */}
        <main className="flex-1 overflow-y-auto p-2 sm:p-3">
          {blockedByOfflineNode ? <NodeOfflineNotice node={selectedNode!} /> : <Outlet />}
        </main>
      </div>
      {nodeConnecting && selectedNode && (
        <ConnectingOverlay
          message={`Connecting to ${selectedNode.name}…`}
          onSkip={dismissConnecting}
        />
      )}
    </div>
    </TitleBarProvider>
    </NodeStreamProvider>
  );
}
